import React, { useState, useEffect } from 'react';
import { useWords } from '../hooks/useWords';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { speak, isSpeechSupported } from '../utils/speak';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { WordDetailsModal } from '../components/vocabulary/WordDetailsModal';

const MagicWandIcon = () => (
  <svg width="20px" height="20px" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#292F33" d="M3.651 29.852L29.926 3.576c.391-.391 2.888 2.107 2.497 2.497L6.148 32.349c-.39.391-2.888-2.107-2.497-2.497z"/>
    <path fill="#66757F" d="M30.442 4.051L4.146 30.347l.883.883L31.325 4.934z"/>
    <path fill="#E1E8ED" d="M34.546 2.537l-.412-.412l-.671-.671a.967.967 0 0 0-.255-.169a.988.988 0 0 0-1.159.169l-2.102 2.102l.495.495l.883.883l1.119 1.119l2.102-2.102a.999.999 0 0 0 0-1.414zM5.029 31.23l-.883-.883l-.495-.495l-2.209 2.208a.988.988 0 0 0-.169 1.159c.046.09.094.18.169.255l.671.671l.412.412a.999.999 0 0 0 1.414 0l2.208-2.208l-1.118-1.119z"/>
    <path fill="#F5F8FA" d="M31.325 4.934l2.809-2.809l-.671-.671a.967.967 0 0 0-.255-.169l-2.767 2.767l.884.882zM4.146 30.347L1.273 33.22c.046.09.094.18.169.255l.671.671l2.916-2.916l-.883-.883z"/>
    <path d="M28.897 14.913l1.542-.571l.6-2.2a.667.667 0 0 1 1.287 0l.6 2.2l1.542.571a.665.665 0 0 1 0 1.25l-1.534.568l-.605 2.415a.667.667 0 0 1-1.293 0l-.605-2.415l-1.534-.568a.665.665 0 0 1 0-1.25M11.961 5.285l2.61-.966l.966-2.61a1.103 1.103 0 0 1 2.07 0l.966 2.61l2.609.966a1.103 1.103 0 0 1 0 2.07l-2.609.966l-.966 2.61a1.105 1.105 0 0 1-2.07 0l-.966-2.61l-2.61-.966a1.104 1.104 0 0 1 0-2.07M24.13 20.772l1.383-.512l.512-1.382a.585.585 0 0 1 1.096 0l.512 1.382l1.382.512a.584.584 0 0 1 0 1.096l-1.382.512l-.512 1.382a.585.585 0 0 1-1.096 0l-.512-1.382l-1.383-.512a.585.585 0 0 1 0-1.096" fill="#FFAC33"/>
  </svg>
);

const HiddenCell = ({ onClick }) => (
  <span
    onClick={onClick}
    className="inline-flex items-center justify-center px-3 py-1 rounded border-2 bg-gray-100 border-gray-300 hover:bg-gray-200 hover:border-gray-400 cursor-pointer select-none transition-colors"
    title="Click to reveal"
  >
    <MagicWandIcon />
  </span>
);

const REVIEW_COUNT = 10;

const ReviewPage = () => {
  const { words, isLoading, hasMore, loadMore } = useWords();
  const [hideMode, setHideMode] = useLocalStorage('review.hideMode', 'translations'); // 'words' | 'translations' | 'none'
  const [revealed, setRevealed] = useState({});
  const [shuffled, setShuffled] = useState(false);
  const [shuffledOrder, setShuffledOrder] = useState([]);
  const [selectedWord, setSelectedWord] = useState(null);

  const wordsWithTranslation = words
    .filter(w => w.translation?.text && (w.learningLevel === 'learning' || w.learningLevel === 'hard'))
    .slice(0, REVIEW_COUNT);

  // If we don't have enough qualifying words yet and there are more pages, load them.
  // words.length > 0 guard prevents firing before the initial fetch completes,
  // which would cause a duplicate request racing with useWords' own initial fetch.
  useEffect(() => {
    if (!isLoading && hasMore && words.length > 0 && wordsWithTranslation.length < REVIEW_COUNT) {
      loadMore();
    }
  }, [words, isLoading, hasMore]);

  const displayedWords = shuffled && shuffledOrder.length
    ? shuffledOrder.filter(w => w.translation?.text)
    : wordsWithTranslation;

  const handleShuffle = () => {
    const copy = [...wordsWithTranslation].sort(() => Math.random() - 0.5);
    setShuffledOrder(copy);
    setShuffled(true);
    setRevealed({});
  };

  const toggleReveal = (wordId, side) => {
    setRevealed(prev => ({
      ...prev,
      [`${wordId}-${side}`]: !prev[`${wordId}-${side}`],
    }));
  };

  const handleHideModeChange = (mode) => {
    setHideMode(mode);
    setRevealed({});
  };

  const isHidden = (wordId, side) => {
    if (hideMode === 'none') return false;
    if (hideMode !== side) return false;
    return !revealed[`${wordId}-${side}`];
  };

  if (isLoading && wordsWithTranslation.length === 0) {
    return (
      <div className="max-w-3xl mx-auto flex justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isLoading && wordsWithTranslation.length === 0) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16 text-gray-500">
        <p className="text-lg font-medium text-gray-700">No words to review.</p>
        <p className="text-sm mt-1">Words with Learning or Hard status and a translation will appear here.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <WordDetailsModal
        isOpen={!!selectedWord}
        onClose={() => setSelectedWord(null)}
        word={selectedWord}
      />
      {/* Controls */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <span className="text-sm text-gray-600 font-medium">Hide:</span>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          {[
            { value: 'words', label: 'Words' },
            { value: 'translations', label: 'Translations' },
            { value: 'none', label: 'Show All' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => handleHideModeChange(opt.value)}
              className={`px-4 py-1.5 text-sm font-medium transition-colors border-l first:border-l-0 border-gray-300 ${
                hideMode === opt.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={handleShuffle}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
          title="Shuffle words"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 3h5v5M4 20L21 3M16 21h5v-5M15 15l6 6M4 4l5 5" />
          </svg>
          Shuffle
        </button>
        <span className="text-xs text-gray-400">{wordsWithTranslation.length} words</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full table-fixed divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-1/2 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Word</th>
              <th className="w-1/2 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Translation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {displayedWords.map(word => (
              <tr key={word.wordId} className="hover:bg-gray-50">
                {/* Word column */}
                <td className="px-4 py-3">
                  {hideMode === 'words' ? (
                    revealed[`${word.wordId}-words`] ? (
                      <span
                        onClick={() => toggleReveal(word.wordId, 'words')}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded border-2 bg-primary-50 border-primary-400 text-primary-700 cursor-pointer select-none transition-colors max-w-full overflow-hidden"
                        title="Click to hide"
                      >
                        <span className="text-sm font-medium break-all">{word.word}</span>
                        {isSpeechSupported() && (
                          <button onClick={(e) => { e.stopPropagation(); speak(word.word); }} className="flex-shrink-0 text-primary-400 hover:text-primary-600 transition-colors" title="Pronounce">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6a7 7 0 010 12M8 9l3-3v12l-3-3H5a1 1 0 01-1-1v-4a1 1 0 011-1h3z" />
                            </svg>
                          </button>
                        )}
                      </span>
                    ) : (
                      <HiddenCell onClick={() => toggleReveal(word.wordId, 'words')} />
                    )
                  ) : (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        onClick={() => setSelectedWord(word)}
                        className="text-sm font-medium text-gray-900 break-all cursor-pointer hover:text-primary-600"
                      >{word.word}</span>
                      {isSpeechSupported() && (
                        <button onClick={() => speak(word.word)} className="flex-shrink-0 text-gray-400 hover:text-primary-600 transition-colors" title="Pronounce">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6a7 7 0 010 12M8 9l3-3v12l-3-3H5a1 1 0 01-1-1v-4a1 1 0 011-1h3z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </td>
                {/* Translation column */}
                <td className="px-4 py-3">
                  {hideMode === 'translations' ? (
                    revealed[`${word.wordId}-translations`] ? (
                      <span
                        onClick={() => toggleReveal(word.wordId, 'translations')}
                        className="inline-flex items-center px-3 py-1 rounded border-2 bg-primary-50 border-primary-400 text-primary-700 cursor-pointer select-none transition-colors max-w-full overflow-hidden"
                        title="Click to hide"
                      >
                        <span className="text-sm italic break-all">{word.translation.text}</span>
                      </span>
                    ) : (
                      <HiddenCell onClick={() => toggleReveal(word.wordId, 'translations')} />
                    )
                  ) : (
                    <span
                      onClick={() => setSelectedWord(word)}
                      className="text-sm text-gray-700 italic break-all cursor-pointer hover:text-primary-600"
                    >{word.translation.text}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReviewPage;
